import _ from "lodash";
import {
  Tabs,
  Group,
  Text,
  Card,
  Badge,
  Button,
  Modal,
  NumberInput,
  Textarea,
  Stack,
  Alert,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconGridDots,
  IconCurrencyDollar,
  IconArrowsExchange,
  IconAlertCircle,
} from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { useServerApi } from "../hooks/useServerApi";
import { PageList } from "./pageList";
import { useCellRender } from "../hooks/useCellRender";
import RemoteSelect2 from "./remoteSelect2";
import moment from "moment";
import { useState, useEffect } from "react";

const COMMISSION_STATUS = ["OPENING", "RELEASED", "SETTLED", "TERMINATED"];

const UserGroupWalletInfo = ({ form }) => {
  const walletId =
    _.get(form.values, "wallet._id") || _.get(form.values, "wallet");
  const groupName = _.get(form.values, "name") || "";
  const groupCode = _.get(form.values, "code") || "";
  const walletName = groupCode ? `WT_${groupCode}` : "";

  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [activeStatus, setActiveStatus] = useState(COMMISSION_STATUS[0]);
  const [summary, setSummary] = useState({ total: 0, totalAmount: 0 });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const [transferOpened, { open: openTransfer, close: closeTransfer }] =
    useDisclosure(false);
  const [transferToClient, setTransferToClient] = useState(null);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferRemark, setTransferRemark] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState("");

  const fetchOpeningBalance = async () => {
    if (!walletId) return;
    try {
      setLoadingBalance(true);
      const result = await api.Wallet.getOpeningBalance(walletId);
      if (result && result.success) {
        setOpeningBalance(result.data.balance ?? 0);
      } else {
        setOpeningBalance(0);
      }
    } catch (e) {
      setOpeningBalance(0);
    } finally {
      setLoadingBalance(false);
    }
  };

  useEffect(() => {
    fetchOpeningBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId]);

  useEffect(() => {
    if (!walletId || !activeStatus) return;
    let cancelled = false;
    setSummary({ total: 0, totalAmount: 0 });
    setLoadingSummary(true);
    const loadSummary = async () => {
      try {
        const q = { wallet: walletId, status: activeStatus };
        const data = await api.search({
          apiEntity: "bid",
          pageSize: 9999,
          currentPage: 1,
          sort: { by: "createdAt", order: "desc" },
          searchQuery: q,
          preQuery: q,
        });
        if (cancelled) return;
        const docs = data.docs ?? [];
        const totalAmount = docs.reduce(
          (sum, d) => sum + (Number(d.paidAmount) || 0),
          0
        );
        setSummary({ total: data.total ?? 0, totalAmount });
      } catch (e) {
        if (!cancelled) setSummary({ total: 0, totalAmount: 0 });
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };
    loadSummary();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId, activeStatus]);

  const handleOpenTransfer = () => {
    setTransferToClient(null);
    setTransferAmount("");
    setTransferRemark("");
    setTransferError("");
    openTransfer();
  };

  const handleTransferSubmit = async () => {
    setTransferError("Transfer is not supported for group wallets yet.");
  };

  const tableSchema = {
    showActionColumn: false,
    searchableFields: ["code", "orderCode"],
    columns: [
      {
        field: "type",
        headerName: "Type",
        sortable: true,
        cellRender: (col, row) => (
          <Badge color="green" size="sm" variant="light">
            {row.type ?? "COMMISSION"}
          </Badge>
        ),
      },
      {
        field: "code",
        headerName: "Bid Code",
        sortable: true,
      },
      {
        field: "level",
        headerName: "Level",
        sortable: true,
      },
      {
        field: "createdAt",
        headerName: "Create At",
        sortable: true,
        cellRender: (col, row) =>
          row.createdAt
            ? moment(row.createdAt).format("MMM DD, YYYY h:mm A")
            : "",
      },
      {
        field: "paidAmount",
        headerName: "Amount",
        sortable: true,
        cellRender: (col, row) => cellRender.Currency(row.paidAmount),
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
      },
    ],
  };

  if (!walletId) {
    return (
      <Text c="dimmed" size="sm">
        No wallet found for this group. Save the group first to auto-create a
        wallet.
      </Text>
    );
  }

  return (
    <>
      {/* Wallet name */}
      <Text
        component="span"
        size="sm"
        fw={500}
        style={{
          borderBottom: "2px solid #0d9488",
          paddingBottom: 2,
          display: "inline-block",
          marginBottom: 16,
        }}
      >
        {walletName}
      </Text>

      {/* Opening balance + Transfer button row */}
      <Group mb="lg" align="flex-start">
        <Card withBorder padding="md" radius="md" style={{ minWidth: 320 }}>
          <Group gap="sm">
            <IconCurrencyDollar size={22} color="#0d9488" stroke={1.5} />
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                OPENING BALANCE
              </Text>
              <Text fw={600}>
                {loadingBalance ? "—" : cellRender.Currency(openingBalance)}
              </Text>
            </div>
          </Group>
        </Card>

        <Button
          leftSection={<IconArrowsExchange size={16} />}
          variant="light"
          color="teal"
          onClick={handleOpenTransfer}
          mt={4}
        >
          Transfer Amount
        </Button>
      </Group>

      {/* Commission tabs */}
      <Tabs value={activeStatus} onChange={setActiveStatus} mb="md">
        <Tabs.List>
          {COMMISSION_STATUS.map((status) => (
            <Tabs.Tab key={status} value={status}>
              {status}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Group gap="lg" mt="md" mb="md">
          <Card withBorder padding="md" radius="md" style={{ minWidth: 220 }}>
            <Group gap="sm">
              <IconGridDots size={22} color="#0d9488" stroke={1.5} />
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  BIDS
                </Text>
                <Text fw={600}>
                  {loadingSummary && summary.total === 0
                    ? "—"
                    : summary.total.toLocaleString()}
                </Text>
              </div>
            </Group>
          </Card>
          <Card withBorder padding="md" radius="md" style={{ minWidth: 280 }}>
            <Group gap="sm">
              <IconCurrencyDollar size={22} color="#eab308" stroke={1.5} />
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  TOTAL AMOUNT
                </Text>
                <Text fw={600}>
                  {loadingSummary && summary.total === 0
                    ? "—"
                    : cellRender.Currency(summary.totalAmount)}
                </Text>
              </div>
            </Group>
          </Card>
        </Group>

        <Tabs.Panel value={activeStatus} pt="xs">
          <PageList
            key={activeStatus}
            apiEntity="bid"
            preQuery={{ wallet: walletId, status: activeStatus }}
            preQueryNotEmpty={true}
            tableSchema={tableSchema}
            hideSearchBar={false}
            hideActionButton={true}
            withPaper={false}
            pt={0}
            px={0}
          />
        </Tabs.Panel>
      </Tabs>

      {/* Transfer Amount Modal */}
      <Modal
        opened={transferOpened}
        onClose={closeTransfer}
        title="Transfer Amount"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Current balance:{" "}
            <Text component="span" fw={600} c="teal">
              {cellRender.Currency(openingBalance)}
            </Text>
          </Text>

          <RemoteSelect2
            label="Transfer To (Client)"
            apiEntity="client"
            required
            placeholder="Search client by name / code"
            valueField="_id"
            labelField="name"
            labelRender={(data) =>
              `${data.name}${data.cname ? " " + data.cname : ""} [${data.code ?? ""}]`
            }
            multiple={false}
            searchFields={["name", "cname", "code"]}
            clearable
            value={transferToClient}
            onDataChange={(data) => setTransferToClient(data?._id ?? null)}
          />

          <NumberInput
            label="Amount"
            placeholder="Enter amount"
            required
            min={0.01}
            max={openingBalance}
            value={transferAmount}
            onChange={(val) => setTransferAmount(val)}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
          />

          <Textarea
            label="Remark (optional)"
            placeholder="Add a note for this transfer"
            value={transferRemark}
            onChange={(e) => setTransferRemark(e.currentTarget.value)}
            rows={2}
          />

          {transferError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
            >
              {transferError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeTransfer}>
              Cancel
            </Button>
            <Button
              color="teal"
              loading={transferring}
              onClick={handleTransferSubmit}
            >
              Confirm Transfer
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default UserGroupWalletInfo;
